import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LogOut, Store, List } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { getAllActivitiesAdmin, deleteActivity, adminLogout } from '../../services/api';
import { toast } from 'sonner';
import { ActivityForm } from './ActivityForm';

export const AdminDashboard = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await getAllActivitiesAdmin();
      setActivities(data);
    } catch (error) {
      console.error('Error loading activities:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        handleLogout();
      } else {
        toast.error('Errore nel caricamento delle attività');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa attività?')) return;

    try {
      await deleteActivity(id);
      toast.success('Attività eliminata con successo');
      loadActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Errore nell\'eliminazione dell\'attività');
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingActivity(null);
    loadActivities();
  };

  if (showForm) {
    return <ActivityForm activity={editingActivity} onClose={handleFormClose} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/veco-logo.jpeg" alt="VeCo Shop" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Gestione Attività Convenzionate</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        
        {/* Actions Bar (Modificato) */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Attività ({activities.length})</h2>
            <p className="text-sm text-gray-600 mt-1">Gestisci tutte le attività convenzionate</p>
          </div>
          
          {/* Gruppo Pulsanti allineati a destra */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => navigate('/admin/categories')} 
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <List className="w-4 h-4 mr-2" />
              Gestisci Categorie
            </Button>
            
            <Button
              onClick={() => setShowForm(true)}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Attività
            </Button>
          </div>
        </div>

        {/* Activities List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Caricamento...</p>
          </div>
        ) : activities.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Nessuna attività presente</p>
              <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700">
                Aggiungi Prima Attività
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((activity) => (
              <Card key={activity.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="relative h-48">
                    <img
                      src={activity.image}
                      alt={activity.name}
                      className="w-full h-full object-cover rounded-t-lg"
                    />
                    {activity.featured && (
                      <Badge className="absolute top-3 right-3 bg-green-500">
                        In Evidenza
                      </Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <Badge variant="outline" className="mb-2">
                      {activity.category}
                    </Badge>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {activity.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">{activity.zone}</p>
                    <p className="text-sm font-semibold text-teal-600 mb-4">
                      {activity.discount}
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(activity)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(activity.id)}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};